import axios from 'axios';

// Dedicated axios instance for Finance Requests endpoints. Same auth as the rest
// of the platform (single sign-on across all apps).
const financeApi = axios.create({ baseURL: '/api/finance-requests', withCredentials: true });

financeApi.interceptors.request.use(cfg => {
  const token = localStorage.getItem('flowup_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

financeApi.interceptors.response.use(
  res => res.data,
  err => {
    if (err.response?.status === 401) {
      const onLogin = window.location.pathname === '/login';
      if (!onLogin) {
        localStorage.removeItem('flowup_token');
        window.dispatchEvent(new CustomEvent('auth:logout'));
        setTimeout(() => { window.location.replace('/login'); }, 100);
      }
    }
    return Promise.reject(err.response?.data || { error: err.message });
  }
);

financeApi.get = (url, config) => financeApi({ method: 'get', url, ...config });
financeApi.post = (url, data, config) => financeApi({ method: 'post', url, data, ...config });
financeApi.put = (url, data, config) => financeApi({ method: 'put', url, data, ...config });
financeApi.delete = (url, config) => financeApi({ method: 'delete', url, ...config });

export default financeApi;

// Workflow status display (label + colours).
export const FR_STATUS = {
  draft:            { label: 'Draft',                    color: '#8A93A6', bg: 'rgba(138,147,166,0.12)' },
  submitted:        { label: 'Submitted',                color: '#6B7280', bg: 'rgba(107,114,128,0.12)' },
  pending_manager:  { label: 'Pending Manager Approval', color: '#D97706', bg: 'rgba(217,119,6,0.12)' },
  manager_approved: { label: 'Manager Approved',         color: '#2563EB', bg: 'rgba(37,99,235,0.12)' },
  manager_rejected: { label: 'Manager Rejected',         color: '#DC2626', bg: 'rgba(220,38,38,0.12)' },
  pending_finance:  { label: 'Pending Finance Approval', color: '#7C3AED', bg: 'rgba(124,58,237,0.12)' },
  finance_approved: { label: 'Finance Approved',         color: '#0D9488', bg: 'rgba(13,148,136,0.12)' },
  finance_rejected: { label: 'Finance Rejected',         color: '#DC2626', bg: 'rgba(220,38,38,0.12)' },
  // Advance / authorization granted, awaiting the employee's settlement.
  awaiting_settlement: { label: 'Awaiting Settlement',   color: '#D97706', bg: 'rgba(217,119,6,0.12)' },
  // Settlement submitted, awaiting the Line Manager's approval (IOU).
  settlement_manager:  { label: 'Settlement — Manager Approval', color: '#D97706', bg: 'rgba(217,119,6,0.12)' },
  // Settlement submitted, awaiting finance confirmation.
  settlement_review:   { label: 'Settlement Review',     color: '#7C3AED', bg: 'rgba(124,58,237,0.12)' },
  // Finance confirmed settlement, awaiting the employee's final confirmation.
  settlement_confirm:  { label: 'Awaiting Your Confirmation', color: '#2563EB', bg: 'rgba(37,99,235,0.12)' },
  completed:        { label: 'Completed',                color: '#16A34A', bg: 'rgba(22,163,74,0.12)' },
  cancelled:        { label: 'Cancelled',                color: '#9CA3AF', bg: 'rgba(156,163,175,0.12)' },
};

export const REQUEST_TYPES = {
  iou:        { label: 'IOU / Cash Advance',          short: 'IOU',        accent: '#0D9488', twoPhase: true },
  expense:    { label: 'Expense Reimbursement',       short: 'Expense',    accent: '#4F46E5', twoPhase: false },
  creditcard: { label: 'Corporate Credit Card',       short: 'Credit Card', accent: '#B45309', twoPhase: true },
};

export const FINANCE_ROLES = [
  { value: 'none', label: 'No access' },
  { value: 'finance_member', label: 'Member' },
  { value: 'finance_manager', label: 'Finance Manager' },
];

export const fmtDMY = (d) => {
  if (!d) return '—';
  const dt = new Date(d); if (isNaN(dt)) return '—';
  const p = (x) => String(x).padStart(2, '0');
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()}`;
};
export const fmtDMYHM = (d) => {
  if (!d) return '—';
  const dt = new Date(d); if (isNaN(dt)) return '—';
  const p = (x) => String(x).padStart(2, '0');
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()} ${p(dt.getHours())}:${p(dt.getMinutes())}`;
};
export const fmtOMR = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
